import { create } from "zustand";
import { persist } from "zustand/middleware";
import { toast } from "react-hot-toast";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import useAuthStore from "../useAuthStore";

const useConsultantAuthStore = create(
  persist(
    (set, get) => ({
      consultant: null,
      token: null,
      isLoading: false,

      // ===========================================================
      // CONSULTANT SIGNUP
      // ===========================================================
      signup: async (consultantData) => {
        set({ isLoading: true });
        try {
          const formData = new FormData();

          // Append all fields to FormData
          Object.keys(consultantData).forEach((key) => {
            if (consultantData[key] !== null && consultantData[key] !== undefined) {
              formData.append(key, consultantData[key]);
            }
          });

          const response = await axiosInstancev1.post("/consultants/auth/signup", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });

          if (response.data.token && response.data.user) {
            const token = response.data.token;
            localStorage.setItem("consultantToken", token);

            set({
              consultant: response.data.user,
              token: token,
            });

            toast.success(response.data.message || "Consultant registered successfully!");
            return true;
          }

          toast.success("Registration successful! Please login.");
          return true;

        } catch (error) {
          const errorMsg = error.response?.data?.message || "Consultant Signup Failed";
          toast.error(errorMsg);
          return false;
        } finally {
          set({ isLoading: false });
        }
      },

      // ===========================================================
      // CONSULTANT LOGIN
      // ===========================================================
      login: async (credential) => {
        set({ isLoading: true });
        try {
          const response = await axiosInstancev1.post("/consultants/auth/login", credential);
          const token = response.data.token;
          const consultantData = response.data.user;

          localStorage.setItem("consultantToken", token);

          set({
            consultant: consultantData,
            token: token,
          });

          toast.success("Consultant Login Successful");
          return true;
        } catch (error) {
          const errorMsg = error.response?.data?.message || "Consultant Login Failed";
          toast.error(errorMsg);
          return false;
        } finally {
          set({ isLoading: false });
        }
      },

      // ===========================================================
      // GET PROFILE
      // ===========================================================
      getProfile: async () => {
        set({ isLoading: true });
        try {
          const token = get().token || localStorage.getItem("consultantToken");
          if (!token) {
            throw new Error("No authentication token found");
          }

          const response = await axiosInstancev1.get("/consultants/auth/profile", {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (response.data.success && response.data.profile) {
            set({ consultant: response.data.profile });
            return response.data.profile;
          }
        } catch (error) {
          console.error("Error fetching consultant profile:", error);
          // toast.error("Failed to fetch profile"); // Omitting toast to avoid noise on background refresh
          return null;
        } finally {
          set({ isLoading: false });
        }
      },

      // ===========================================================
      // UPDATE PROFILE
      // ===========================================================
      updateProfile: async (updateData) => {
        set({ isLoading: true });
        try {
          const token = get().token || localStorage.getItem("consultantToken");
          if (!token) {
            throw new Error("No authentication token found");
          }

          const formData = new FormData();
          Object.keys(updateData).forEach((key) => {
            if (updateData[key] !== null && updateData[key] !== undefined) {
              formData.append(key, updateData[key]);
            }
          });

          const response = await axiosInstancev1.put("/consultants/auth/profile", formData, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          });

          if (response.data.success && response.data.profile) {
            set({ consultant: response.data.profile });
            toast.success("Profile updated successfully!");
            return true;
          }

          return false;
        } catch (error) {
          const errorMsg = error.response?.data?.message || "Profile update failed";
          toast.error(errorMsg);
          return false;
        } finally {
          set({ isLoading: false });
        }
      },

      // ===========================================================
      // SEND EMAIL OTP
      // ===========================================================
      sendEmailOtp: async (email) => {
        set({ isLoading: true });
        try {
          const response = await axiosInstancev1.post("/consultants/auth/send-email-otp", {
            email,
          });

          if (response.data.success) {
            toast.success(response.data.message || "OTP sent to your email");
            return true;
          }
          return false;
        } catch (error) {
          const errorMsg = error.response?.data?.message || "Failed to send OTP";
          toast.error(errorMsg);
          return false;
        } finally {
          set({ isLoading: false });
        }
      },

      // ===========================================================
      // VERIFY EMAIL OTP
      // ===========================================================
      verifyEmailOtp: async (email, otp) => {
        set({ isLoading: true });
        try {
          const response = await axiosInstancev1.post("/consultants/auth/verify-email-otp", {
            email,
            otp,
          });

          if (response.data.success) {
            toast.success(response.data.message || "Email verified successfully");
            return true;
          }
          return false;
        } catch (error) {
          const errorMsg = error.response?.data?.message || "OTP verification failed";
          toast.error(errorMsg);
          return false;
        } finally {
          set({ isLoading: false });
        }
      },

      // ===========================================================
      // LOGOUT
      // ===========================================================
      logout: () => {
        localStorage.removeItem("consultantToken");
        set({ consultant: null, token: null });

        // Also clear global auth store if it contains consultant data
        const globalStore = useAuthStore.getState();
        if (globalStore.userType === "consultant") {
          useAuthStore.setState({ user: null, userType: null, token: null });
        }

        toast.success("Consultant logged out");
      },
    }),
    {
      name: "consultant-auth-storage",
      getStorage: () => localStorage,
    }
  )
);

export default useConsultantAuthStore;
